import { useMemo, useState } from "react";
import type { AdCustomization } from "@services/engagements/commands/cmdRequestContent";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import MotiModal from "@/shared/components/MotiModal";
import AdCard from "@/shared/components/AdCard/AdCard";
import Loader from "@/components/common/loader/loader";
import { appStore } from "@store/appStore";
import { OrganizationService } from "@services/organizations/OrganizationService";
import { EngagementService } from "@services/engagements/engagementService";
import { NotificationService } from "@services/notifications/NotificationService";
import type { ContentCatalogFolder } from "@models/contentCatalogFolder.model";
import type { Ad } from "@models/Ad.model";
import { errorMessage } from "@/shared/utils/errorMessage";

interface Props {
    open: boolean;
    setIsOpen: (open: boolean) => void;
    folders: ContentCatalogFolder[];
}

function collectItemIds(folders: ContentCatalogFolder[]): string[] {
    const ids: string[] = [];
    for (const folder of folders) {
        ids.push(`folder-${folder.id}`);
        for (const ad of folder.ads) ids.push(`ad-${folder.id}-${ad.id}`);
        ids.push(...collectItemIds(folder.children));
    }
    return ids;
}

function renderTree(folders: ContentCatalogFolder[]): React.ReactNode {
    return folders.map((folder) => {
        const hasChildren = folder.children.length > 0;
        return (
        <TreeItem
            key={`folder-${folder.id}`}
            itemId={`folder-${folder.id}`}
            sx={{
                "& > .MuiTreeItem-content": {
                    backgroundColor: hasChildren
                        ? "rgba(132, 90, 223, 0.12) !important"
                        : "rgba(132, 90, 223, 0.06) !important",
                    borderRadius: 1,
                    my: 0.5,
                },
            }}
            label={
                <Typography
                    variant={hasChildren ? "h6" : "subtitle1"}
                    component="div"
                    sx={{ py: hasChildren ? 1 : 0.5, color: "#845adf", fontWeight: 600 }}
                >
                    {folder.name}
                </Typography>
            }
        >
            {hasChildren && renderTree(folder.children)}
            {folder.ads.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, my: 1, pl: 2 }}>
                    {folder.ads.map((ad: Ad) => (
                        <TreeItem
                            key={`ad-${folder.id}-${ad.id}`}
                            itemId={`ad-${folder.id}-${ad.id}`}
                            label={
                                <div style={{ width: 200 }}>
                                    <AdCard ad={ad} mode="compact" menuActions={[]} hideType hideMenu />
                                </div>
                            }
                            sx={{
                                flex: "0 0 auto",
                                "& .MuiTreeItem-content": { justifyContent: "center" },
                                "& .MuiTreeItem-content:hover": { bgcolor: "transparent" },
                                "& .MuiTreeItem-content.Mui-selected": { bgcolor: "transparent" },
                            }}
                        />
                    ))}
                </Box>
            )}
        </TreeItem>
        );
    });
}

export default function CollectionSelector({ open, setIsOpen, folders }: Props) {
    const [selected, setSelected] = useState<string[]>([]);
    const [notesOpen, setNotesOpen] = useState(false);
    const [notes, setNotes] = useState("");
    const [customizations, setCustomizations] = useState<Record<number, AdCustomization>>({});
    const [submitting, setSubmitting] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);
    const [submitError, setSubmitError] = useState("");

    const defaultExpanded = useMemo(() => collectItemIds(folders), [folders]);
    const selectedAdCount = selected.filter((id) => id.startsWith("ad-")).length;

    // The selected GuidePosts, with names, because the modal now labels a card per
    // GuidePost and "Ad 37014" is not something a client can tell apart from another
    // one (MPN-1622). Ids alone were enough while the note was shared.
    const selectedAds = useMemo(() => {
        const byId = new Map<number, string>();
        const walk = (list: any[]) => {
            for (const folder of list) {
                for (const ad of folder.ads ?? []) byId.set(ad.id, ad.name);
                if (folder.children?.length) walk(folder.children);
            }
        };
        walk(folders as any[]);
        return Array.from(new Set(
            selected
                .filter((id) => id.startsWith("ad-"))
                .map((id) => Number(id.split("-")[2]))
                .filter((n) => !Number.isNaN(n)),
        )).map((id) => ({ id, name: byId.get(id) ?? `Ad ${id}` }));
    }, [selected, folders]);

    const setField = (adId: number, field: keyof AdCustomization, value: string) =>
        setCustomizations((prev) => ({
            ...prev,
            [adId]: { ...(prev[adId] ?? {}), [field]: value },
        }));

    const close = () => {
        setIsOpen(false);
        setSelected([]);
    };

    const submitRequest = async () => {
        const adIds = Array.from(new Set(
            selected
                .filter((id) => id.startsWith("ad-"))
                .map((id) => Number(id.split("-")[2]))
                .filter((n) => !Number.isNaN(n)),
        ));

        if (adIds.length === 0) return;

        setSubmitting(true);
        setSubmitError("");
        try {
            const clientId = appStore.loggedInUser.organization.id;
            const defaultOrg = await new OrganizationService().getDefaultOrganization();
            if (!defaultOrg) throw new Error("No default organization found");

            const engagement = await new EngagementService().requestContent(
                defaultOrg.id,
                clientId,
                { adIds, additionalNotes: notes, customizations },
            );

            await new NotificationService().send_notification("ENGAGEMENT_CREATED", {
                engagement: { id: engagement.id },
            });

            setNotes("");
            setNotesOpen(false);
            close();
            setSuccessOpen(true);
        } catch (err) {
            // The dialog stays open on failure. Closing it before the request ran
            // put the user back on the selection step with no message and their
            // typed customizations still in state, which reads as a no-op loop
            // rather than a refusal (MPN-1881).
            console.error("Error submitting content request:", err);
            setSubmitError(errorMessage(err, "Could not submit your content request."));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <MotiModal isOpen={open} setIsOpen={setIsOpen}>
                <MotiModal.Header>
                    <Typography sx={{ ml: 2, flex: 1, color: "#111827" }} variant="h6">
                        Select Content
                    </Typography>
                </MotiModal.Header>
                <MotiModal.Body>
                    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "#fff" }}>
                        <Box sx={{ flex: 1, overflow: "auto", p: 3, bgcolor: "#fff" }}>
                            <SimpleTreeView
                                multiSelect
                                checkboxSelection
                                defaultExpandedItems={defaultExpanded}
                                selectionPropagation={{ descendants: true, parents: true }}
                                onSelectedItemsChange={(_, ids) => setSelected(ids)}
                                sx={{
                                    "& .Mui-checked": { color: "#845adf" },
                                    "& .MuiCheckbox-indeterminate": { color: "#845adf" },
                                }}
                            >
                                {renderTree(folders)}
                            </SimpleTreeView>
                        </Box>
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 2,
                            borderTop: "1px solid #e5e7eb",
                            bgcolor: "#fff",
                        }}>
                            <Typography variant="h6">Selected {selectedAdCount} Ad(s)</Typography>
                            <Button
                                variant="contained"
                                disabled={selectedAdCount === 0 || submitting}
                                onClick={() => setNotesOpen(true)}
                            >
                                {submitting && <Loader />}Submit Request
                            </Button>
                        </Box>
                    </Box>
                </MotiModal.Body>
            </MotiModal>

            <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Customization Instructions</DialogTitle>
                <DialogContent>
                    {submitError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>
                    )}
                    {/*
                        Said before anything is asked for, because the commonest request
                        on the old shared box was for the two things that already happen
                        automatically (MPN-1622).
                    */}
                    <Typography variant="body2" sx={{ mb: 2, color: "#475569" }}>
                        Your logo and brand colors will always be applied. If further
                        customization is desired, please indicate your preferences below.
                    </Typography>

                    {/*
                        A card per GuidePost. The single shared field this replaces made it
                        impossible to tell which instruction belonged to which piece of
                        content once a client selected more than one.
                    */}
                    {selectedAds.map((ad) => (
                        <Box
                            key={ad.id}
                            sx={{
                                mb: 2, p: 2,
                                border: "1px solid #e5e7eb", borderRadius: 1,
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                {ad.name}
                            </Typography>
                            <TextField
                                fullWidth size="small" sx={{ mb: 1.5 }}
                                label="Preferred naming conventions"
                                placeholder='e.g. "admissions" vs. "enrollment"'
                                value={customizations[ad.id]?.preferredNaming ?? ""}
                                onChange={(e) => setField(ad.id, "preferredNaming", e.target.value)}
                            />
                            <TextField
                                fullWidth size="small" sx={{ mb: 1.5 }}
                                label="Preferred CTA"
                                value={customizations[ad.id]?.preferredCta ?? ""}
                                onChange={(e) => setField(ad.id, "preferredCta", e.target.value)}
                            />
                            <TextField
                                fullWidth size="small" multiline minRows={2} sx={{ mb: 1.5 }}
                                label="Additional messaging notes"
                                value={customizations[ad.id]?.messagingNotes ?? ""}
                                onChange={(e) => setField(ad.id, "messagingNotes", e.target.value)}
                            />
                            <TextField
                                fullWidth size="small" multiline minRows={2}
                                label="Image/Video"
                                helperText="If you'd like to swap the image or video: please include a link to your own video or provide guidance on what you'd like to see."
                                value={customizations[ad.id]?.imageVideoNotes ?? ""}
                                onChange={(e) => setField(ad.id, "imageVideoNotes", e.target.value)}
                            />
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNotesOpen(false)} disabled={submitting}>Cancel</Button>
                    <Button variant="contained" onClick={submitRequest} disabled={submitting}>
                        {submitting && <Loader />}Submit
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={successOpen} onClose={() => setSuccessOpen(false)}>
                <Box p={3}>
                    <Typography variant="h6">Request Sent</Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                        Your content request has been submitted successfully.
                    </Typography>
                    <Button onClick={() => setSuccessOpen(false)} sx={{ mt: 2 }} variant="contained">
                        OK
                    </Button>
                </Box>
            </Dialog>
        </>
    );
}
